import { preprocessResumeText } from '../../../../../utils/pdfTextSanitizer';
import { restoreResumeStructure } from '../../../../../utils/resumeStructure';

/**
 * CRITICAL: Section capture at upload drives the entire optimization pipeline.
 * - We must capture EVERY section the user has (no dropping or merging away).
 * - Content is then placed in the appropriate section; optimization only TWEAKS per section for ATS/job match.
 * - Benchmark improvement % (formatting, keywords, structure) is calculated per section and overall.
 * Parse splits by markdown headers (# / ##), common section titles, and ALL CAPS headers.
 * Preprocesses run-on lines so section boundaries are preserved.
 */
export interface ResumeSection {
  title: string;
  content: string;
  preview: string;
}

/** Section names for detection — comprehensive so we capture every section (longest first). */
const SECTION_HEADER_NAMES = [
  'professional summary', 'executive summary', 'career summary', 'summary of qualifications', 'summary', 'profile', 'objective', 'career objective',
  'contact information', 'personal information', 'personal details', 'contact details',
  'work experience', 'employment history', 'work history', 'professional experience', 'career history', 'experience', 'employment', 'professional history',
  'education', 'academic background', 'academic qualifications', 'qualifications', 'academic experience', 'teaching experience',
  'technical skills', 'technical proficiencies', 'technical expertise', 'domain expertise', 'product management & design tools',
  'languages & frameworks', 'cloud & devops technologies', 'cloud & devops', 'ml & data science', 'ai/ml & data science',
  'skills', 'key skills', 'core competencies', 'competencies', 'certifications', 'certificates', 'professional certifications',
  'awards', 'honors', 'awards & honors', 'recognition', 'achievements', 'key achievements', 'career achievements',
  'projects', 'key projects', 'side projects', 'open source', 'volunteer experience', 'volunteer work', 'community service',
  'publications', 'patents', 'research', 'research experience',
  'professional development', 'training', 'courses', 'continuing education', 'professional training',
  'leadership', 'activities', 'extracurricular', 'affiliations', 'professional affiliations', 'memberships',
  'references', 'additional information', 'additional details', 'expertise', 'strengths', 'hobbies', 'interests',
  'goal', 'contact', 'languages', 'foreign languages', 'technologies', 'frameworks', 'market strategy', 'tools', 'software', 'methodologies'
];

/** Leading list markers including Word/PDF Wingdings (). */
const LEADING_BULLET_RE = /^[#]*\s*[•*\-▪∙●◦○·\u2022\uF0B7\uF0A7]+\s*/;

/** Strip markdown headers and leading/trailing bullet chars so "• Work Experience" matches "work experience". */
function normalizeTitle(line: string): string {
  return line.replace(/^#+\s*/, '').replace(LEADING_BULLET_RE, '').replace(/\s*[•*\-]+$/g, '').replace(/\*+/g, '').trim();
}

/** Longest-first order for matching so "work experience" wins over "experience". */
const SECTION_HEADER_NAMES_SORTED = [...SECTION_HEADER_NAMES].sort((a, b) => b.length - a.length);
const SECTION_HEADER_SET = new Set(SECTION_HEADER_NAMES);

/** Incomplete first-half of a wrapped header (e.g. "Languages &", "Technical"). */
const HEADER_JOIN_PREFIXES = new Set([
  'technical', 'professional', 'career', 'work', 'personal', 'contact', 'key', 'core',
  'academic', 'languages', 'languages &', 'cloud', 'cloud &', 'ai/ml', 'ai/ml &',
  'ml', 'ml &', 'product management', 'product management &', 'employment', 'additional',
  'foreign', 'spoken', 'teaching', 'volunteer', 'community',
]);

function stripHeaderDecor(line: string): string {
  return line
    .replace(/^#+\s*/, '')
    .replace(LEADING_BULLET_RE, '')
    .replace(/[:.\s]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Anchored header test — the whole line (after trim/casing) must be a known section title. */
function isSectionHeader(line: string): boolean {
  const n = stripHeaderDecor(line).toLowerCase();
  if (!n || n.length > 60) return false;
  return SECTION_HEADER_SET.has(n);
}

function looksLikeHeaderFragment(line: string): boolean {
  const n = stripHeaderDecor(line).toLowerCase();
  if (!n || n.length > 40) return false;
  if (SECTION_HEADER_SET.has(n)) return false;
  if (/[&/]$/.test(n)) return true;
  return HEADER_JOIN_PREFIXES.has(n);
}

function tryJoinHeaderTitles(a: string, b: string): string | null {
  const left = stripHeaderDecor(a);
  const right = stripHeaderDecor(b);
  if (!left || !right) return null;
  const joined = `${left} ${right}`.replace(/\s+/g, ' ').trim();
  if (isSectionHeader(joined)) return joined;
  const withAmp = `${left.replace(/\s*&\s*$/, '')} & ${right.replace(/^&\s*/, '')}`.replace(/\s+/g, ' ').trim();
  if (isSectionHeader(withAmp)) return withAmp;
  return null;
}

function mergeSplitHeaderSections(sections: ResumeSection[]): ResumeSection[] {
  const out: ResumeSection[] = [];
  for (let i = 0; i < sections.length; i++) {
    const curr = sections[i];
    const next = sections[i + 1];
    const firstContentLine = (curr.content.split(/\r?\n/).find((l) => l.trim()) || '').trim();
    const joinedWithNext = next ? tryJoinHeaderTitles(curr.title, next.title) : null;
    const joinedWithContent = tryJoinHeaderTitles(curr.title, firstContentLine);
    if (joinedWithNext) {
      const leftover = [curr.content, next.content]
        .map((c) => c.trim())
        .filter((c) => c && !/^[&/\s]+$/.test(c) && !isSectionHeader(c));
      const content = leftover.join('\n').trim();
      out.push({
        title: joinedWithNext,
        content,
        preview: content.slice(0, 100).replace(/\n/g, ' ') + (content.length > 100 ? '…' : ''),
      });
      i++;
      continue;
    }
    if (joinedWithContent) {
      const rest = curr.content.split(/\r?\n/).slice(1).join('\n').trim();
      out.push({
        title: joinedWithContent,
        content: rest,
        preview: rest.slice(0, 100).replace(/\n/g, ' ') + (rest.length > 100 ? '…' : ''),
      });
      continue;
    }
    out.push(curr);
  }
  return out;
}

function joinSplitHeaderLines(rawLines: string[]): string[] {
  const lines: string[] = [];
  for (let i = 0; i < rawLines.length; i++) {
    const curr = rawLines[i];
    const next = rawLines[i + 1];
    if (next != null) {
      const joinedTitle = tryJoinHeaderTitles(curr, next);
      const currCaps = /^[A-Z]{3,}(\s[A-Z&/\-]{2,})*$/.test(curr.trim()) && curr.trim().length <= 30;
      const nextCaps = /^[A-Z]{3,}(\s[A-Z&/\-]{2,})*$/.test(next.trim()) && next.trim().length <= 30;
      if (joinedTitle) {
        lines.push(joinedTitle);
        i++;
        continue;
      }
      if (currCaps && nextCaps) {
        lines.push(`${curr.trim()} ${next.trim()}`);
        i++;
        continue;
      }
    }
    lines.push(curr);
  }
  return lines;
}

function isBulletLine(line: string): boolean {
  return /^[•*\-▪∙●◦○·\u2022\uF0B7\uF0A7]\s+\S/.test(line.trim());
}

/**
 * Rejoin wrapped paragraph fragments so a mid-sentence line break does not
 * become a new section or orphan token. Headers, bullets, job dates, and
 * contact lines stay on their own lines.
 */
function stitchWrappedContentLines(rawLines: string[]): string[] {
  const out: string[] = [];
  for (const line of rawLines) {
    const t = line.trim();
    if (!t) {
      out.push('');
      continue;
    }
    if (isSectionHeader(t) || looksLikeHeaderFragment(t) || isBulletLine(t) || isJobHistoryLine(t)) {
      out.push(line);
      continue;
    }
    const prev = out[out.length - 1];
    const prevTrim = prev?.trim() ?? '';
    if (
      prevTrim &&
      !isSectionHeader(prevTrim) &&
      !looksLikeHeaderFragment(prevTrim) &&
      !isBulletLine(prevTrim) &&
      !isJobHistoryLine(prevTrim) &&
      !/[.!?]$/.test(prevTrim)
    ) {
      const startsLower = /^[a-z]/.test(t);
      const prevEndsMid = /[,;:&]|(\b(and|or|of|the|to|for|with|in|on|at)$)/i.test(prevTrim);
      if (startsLower || prevEndsMid) {
        out[out.length - 1] = `${prevTrim} ${t}`;
        continue;
      }
    }
    out.push(line);
  }
  return out;
}

const JOB_DATE_RANGE_RE =
  /\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+'?\d{2,4}\s*[-–—]\s*(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+'?\d{2,4}\b|\b\d{4}\s*[-–—]\s*(?:\d{4}|present|current|till\s*date)\b|\b\d{1,2}\/\d{4}\s*[-–—]\s*(?:\d{1,2}\/\d{4}|present|till\s*date)\b/i;

function isJobHistoryLine(line: string): boolean {
  const t = line.trim();
  if (!t) return false;
  if (JOB_DATE_RANGE_RE.test(t)) return true;
  if (/\b\d{2}\/\d{4}\b/.test(t) && /\b(at|–|-|\|)\b/.test(t)) return true;
  return false;
}

const DESIGN_OR_TOOL_RE =
  /\b(figma|miro|productboard|balsamiq|sketch|invision|zeplin|notion|jira|asana|trello|canva|framer|amplitude|hotjar|maze|overflow|principle|adobe\s*(xd|illustrator|photoshop)?|looker|tableau|mixpanel|confluence|slack|loom)\b/i;

function looksLikeToolListLine(line: string): boolean {
  const t = line.trim();
  if (!t || t.length > 160) return false;
  if (DESIGN_OR_TOOL_RE.test(t)) return true;
  const parts = t.split(/[,|/•]+/).map((p) => p.trim()).filter(Boolean);
  return parts.length >= 2 && parts.every((p) => p.length <= 28 && !/@|\d{4}/.test(p));
}

/** Short section names that are ambiguous — only treat as header after blank line or sentence end. */
const AMBIGUOUS_SECTION_NAMES = new Set([
  'experience', 'expertise', 'technologies', 'technology', 'skills', 'education', 'awards',
  'languages', 'frameworks', 'software', 'summary', 'profile', 'objective', 'contact', 'references',
  // These commonly appear inline/mid-content in bullet points and sentence structures
  'leadership', 'activities', 'achievements', 'strengths', 'hobbies', 'interests',
  'training', 'tools', 'goal', 'methodologies', 'certifications', 'projects',
]);

/** True if next line looks like a sentence continuation (e.g. "in the IT industry" after "experience"). */
function isLikelyContinuationLine(nextLine: string): boolean {
  const t = nextLine.trim();
  if (!t) return false;
  const first = t[0];
  if (first >= 'a' && first <= 'z') return true;
  if (/^(in|and|with|for|on|at|the|to|by)\s+/i.test(t) && t.length < 120) return true;
  return false;
}

/**
 * True only when the entire line is a known section title (or title + colon + short rest).
 * Ambiguous single words still require a blank/sentence-ending previous line.
 */
function isStandaloneSectionHeader(line: string, prevLineTrimmed: string): boolean {
  const trimmed = stripHeaderDecor(line);
  if (!trimmed || trimmed.length > 55) return false;
  const extracted = extractSectionTitleAndRest(line);
  const headerName = extracted
    ? extracted.title.toLowerCase()
    : (SECTION_HEADER_SET.has(trimmed.toLowerCase()) ? trimmed.toLowerCase() : '');
  if (!headerName || !SECTION_HEADER_SET.has(headerName)) return false;
  const prevBlankOrEnd = prevLineTrimmed.length === 0 || /[.!?]\s*$/.test(prevLineTrimmed);
  if (AMBIGUOUS_SECTION_NAMES.has(headerName) && !prevBlankOrEnd) return false;
  return true;
}

/**
 * If the line is exactly a known section name, or "Name: rest", return title + rest.
 * Does not match sentences that merely start with a section word ("Experience leading…").
 */
function extractSectionTitleAndRest(line: string): { title: string; rest: string } | null {
  const trimmed = line.trim().replace(LEADING_BULLET_RE, '').replace(/\s*[•*\-]+$/g, '').trim();
  const lower = trimmed.toLowerCase();
  for (const name of SECTION_HEADER_NAMES_SORTED) {
    if (lower === name || lower === `${name}:`) {
      return { title: trimmed.replace(/[:\s]+$/, '').trim(), rest: '' };
    }
    if (lower.startsWith(`${name}:`)) {
      return { title: trimmed.slice(0, name.length), rest: trimmed.slice(name.length).replace(/^[:\s]+/, '').trim() };
    }
  }
  return null;
}

/**
 * Preprocess text so section headers are on their own line (preserve resume sections).
 * - "Languages & FrameworksJava" -> "Languages & Frameworks\nJava"
 * - "Section Name: content here" -> "Section Name\ncontent here"
 * - Strips leading ** for detection then keeps content.
 */
function preprocessForSectionSplits(text: string): string {
  const lines = text.split(/\r?\n/);
  const out: string[] = [];
  // Sort by length descending so longer names match first
  const sortedNames = [...SECTION_HEADER_NAMES].sort((a, b) => b.length - a.length);

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      out.push(line);
      continue;
    }
    // Strip leading/trailing bullets and * for comparison only
    const forMatch = trimmed.replace(/^[•*\-]+\s*/, '').replace(/\s*[•*\-]+$/, '').trim();
    const lower = forMatch.toLowerCase();
    let didSplit = false;

    for (const name of sortedNames) {
      if (lower.length <= name.length) continue;
      const idx = lower.indexOf(name);
      if (idx !== 0) continue; // section name must be at start of line
      const nameLen = name.length;
      const afterName = forMatch.slice(nameLen);
      // True PDF run-on only: no space between header and next letter ("FrameworksJava").
      // Do NOT split sentences that start with a section word ("Experience in product…").
      const runOn = afterName.length > 0 && /[A-Za-z]/.test(afterName[0]) && afterName[0] !== ':';
      const hasColonContent = /^\s*:/.test(afterName);

      if (runOn) {
        // "Languages & FrameworksJava" -> section on one line, "Java" on next (preserve casing)
        const sectionPart = forMatch.slice(0, idx + nameLen);
        const contentPart = forMatch.slice(idx + nameLen).trim();
        out.push(sectionPart);
        if (contentPart) out.push(contentPart);
        didSplit = true;
        break;
      }
      if (hasColonContent) {
        const contentPart = afterName.replace(/^:?\s*/, '').trim();
        if (contentPart) {
          out.push(forMatch.slice(0, idx + nameLen));
          out.push(contentPart);
          didSplit = true;
          break;
        }
      }
    }
    if (!didSplit) out.push(line);
  }
  return out.join('\n');
}

export function parseResumeSections(fullText: string): ResumeSection[] {
  if (!fullText || typeof fullText !== 'string') return [];

  const cleaned = preprocessResumeText(fullText);
  const withStructure = restoreResumeStructure(cleaned);
  const preprocessed = preprocessForSectionSplits(withStructure);

  // Join wrapped / ALL-CAPS headers that PDF extractors split across two lines
  // e.g. "PROFESSIONAL\nSUMMARY" → "PROFESSIONAL SUMMARY", "Languages &\nFrameworks"
  const rawLines = preprocessed.split(/\r?\n/);
  const joinedHeaders = joinSplitHeaderLines(rawLines);
  const lines = stitchWrappedContentLines(joinedHeaders);

  const sections: ResumeSection[] = [];
  let currentTitle = 'Personal Information';
  let currentContent: string[] = [];

  let prevLineTrimmed = '';
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = stripHeaderDecor(line);
    const nextLine = lines[i + 1] ?? '';
    const asHeader = line.trim().match(/^#+\s+(.+)$/);

    let asNamed: string | null = asHeader ? null : (isStandaloneSectionHeader(line, prevLineTrimmed) ? trimmed : null);
    if (asNamed && AMBIGUOUS_SECTION_NAMES.has(trimmed.toLowerCase()) && isLikelyContinuationLine(nextLine)) {
      asNamed = null;
    }
    const asAllCaps = !asHeader && !asNamed && trimmed.length >= 2 && trimmed.length <= 45
      && /^[A-Z][A-Z0-9\s&/\-]+$/.test(trimmed)
      && isSectionHeader(trimmed);

    if (asHeader || asNamed || asAllCaps) {
      let newTitle: string;
      let restOfLine = '';
      if (asHeader) {
        newTitle = normalizeTitle(asHeader[1]);
      } else {
        const extracted = extractSectionTitleAndRest(line.trim());
        if (extracted) {
          newTitle = extracted.title;
          restOfLine = extracted.rest;
        } else {
          newTitle = normalizeTitle(trimmed);
        }
      }
      const content = currentContent.join('\n').trim();
      if (content || (sections.length === 0 && currentTitle === 'Personal Information')) {
        sections.push({
          title: currentTitle,
          content: content || fullText.slice(0, 400),
          preview: (content || fullText).slice(0, 100).replace(/\n/g, ' ') + ((content || fullText).length > 100 ? '…' : '')
        });
      }
      currentTitle = newTitle;
      currentContent = restOfLine ? [restOfLine] : [];
    } else {
      currentContent.push(line);
    }
    prevLineTrimmed = trimmed;
  }

  const content = currentContent.join('\n').trim();
  sections.push({
    title: currentTitle,
    content: content || '',
    preview: content.slice(0, 100).replace(/\n/g, ' ') + (content.length > 100 ? '…' : '')
  });

  let filtered = sections.filter(s => s.content.length > 0);

  // Merge "Languages" + "Frameworks" when Languages has only "&" or "& Frameworks" (split by mid-line break)
  const merged: ResumeSection[] = [];
  for (let i = 0; i < filtered.length; i++) {
    const curr = filtered[i];
    const contentTrim = curr.content.trim();
    const isLanguagesWithAmp = curr.title.toLowerCase() === 'languages' &&
      (contentTrim === '&' || contentTrim === '& ' || /^&\s*Frameworks?\s*$/i.test(contentTrim) || contentTrim.startsWith('& '));
    const next = filtered[i + 1];
    const nextIsFrameworks = next && next.title.toLowerCase() === 'frameworks';
    if (isLanguagesWithAmp && nextIsFrameworks) {
      merged.push({
        title: 'Languages & Frameworks',
        content: next.content.trim(),
        preview: next.preview
      });
      i++;
    } else {
      merged.push(curr);
    }
  }
  filtered = mergeSplitHeaderSections(merged);

  if (filtered.length === 0 && fullText.trim()) {
    return [{
      title: 'Full Resume',
      content: fullText.trim(),
      preview: fullText.slice(0, 100).replace(/\n/g, ' ') + (fullText.length > 100 ? '…' : '')
    }];
  }
  return smartPostProcessSections(filtered);
}

/**
 * Split resume text by the section headers that actually appear in the document.
 * Returns sections with the document's exact header text as title (no canonical renaming, no bucketing).
 * Use this to capture resume content in the resume's own sections; improve/refine later.
 */
export function splitResumeByDocumentHeaders(fullText: string): ResumeSection[] {
  if (!fullText || typeof fullText !== 'string') return [];

  const cleaned = preprocessResumeText(fullText);
  const withStructure = restoreResumeStructure(cleaned);
  const preprocessed = preprocessForSectionSplits(withStructure);
  const lines = stitchWrappedContentLines(joinSplitHeaderLines(preprocessed.split(/\r?\n/)));
  const sections: ResumeSection[] = [];
  let currentTitle = 'Resume';
  let currentContent: string[] = [];

  let prevLineTrimmed = '';
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim().replace(/^[•*\-]+\s*|\s*[•*\-]+$/g, '').trim();
    const nextLine = lines[i + 1] ?? '';
    const asHeader = trimmed.match(/^#+\s+(.+)$/);
    let asNamed: string | null = asHeader ? null : (isStandaloneSectionHeader(trimmed, prevLineTrimmed) ? trimmed : null);
    // Don't treat ambiguous single-word line as header if next line looks like sentence continuation (e.g. "experience" then "in the IT industry")
    if (asNamed && AMBIGUOUS_SECTION_NAMES.has(trimmed.toLowerCase()) && isLikelyContinuationLine(nextLine)) {
      asNamed = null;
    }
    const asAllCaps = !asHeader && !asNamed && trimmed.length >= 2 && trimmed.length <= 45
      && /^[A-Z][A-Z0-9\s&/\-]+$/.test(trimmed)
      && isSectionHeader(trimmed);

    if (asHeader || asNamed || asAllCaps) {
      let newTitle: string;
      let restOfLine = '';
      if (asHeader) {
        newTitle = normalizeTitle(asHeader[1]);
      } else {
        const extracted = extractSectionTitleAndRest(trimmed);
        if (extracted) {
          newTitle = extracted.title;
          restOfLine = extracted.rest;
        } else {
          newTitle = normalizeTitle(trimmed);
        }
      }
      const content = currentContent.join('\n').trim();
      if (content || (sections.length === 0 && currentTitle === 'Resume')) {
        sections.push({
          title: currentTitle,
          content: content || fullText.slice(0, 400),
          preview: (content || fullText).slice(0, 100).replace(/\n/g, ' ') + ((content || fullText).length > 100 ? '…' : ''),
        });
      }
      currentTitle = newTitle;
      currentContent = restOfLine ? [restOfLine] : [];
    } else {
      currentContent.push(line);
    }
    prevLineTrimmed = trimmed;
  }

  const content = currentContent.join('\n').trim();
  sections.push({
    title: currentTitle,
    content: content || '',
    preview: content.slice(0, 100).replace(/\n/g, ' ') + (content.length > 100 ? '…' : ''),
  });

  let filtered = sections.filter((s) => s.content.length > 0);

  // Merge run-on "Languages" + "Frameworks" only (document structure fix, not renaming)
  const merged: ResumeSection[] = [];
  for (let i = 0; i < filtered.length; i++) {
    const curr = filtered[i];
    const contentTrim = curr.content.trim();
    const isLanguagesWithAmp = curr.title.toLowerCase() === 'languages' &&
      (contentTrim === '&' || contentTrim === '& ' || /^&\s*Frameworks?\s*$/i.test(contentTrim) || contentTrim.startsWith('& '));
    const next = filtered[i + 1];
    const nextIsFrameworks = next && next.title.toLowerCase() === 'frameworks';
    if (isLanguagesWithAmp && nextIsFrameworks) {
      merged.push({
        title: 'Languages & Frameworks',
        content: next.content.trim(),
        preview: next.preview,
      });
      i++;
    } else {
      merged.push(curr);
    }
  }
  filtered = merged;

  // Merge sections with same normalized title (e.g. all "experience"/"Experience" -> one section)
  const byNormalizedTitle = new Map<string, ResumeSection>();
  for (const s of filtered) {
    const key = s.title.toLowerCase().trim();
    const existing = byNormalizedTitle.get(key);
    if (existing) {
      existing.content = [existing.content, s.content].filter(Boolean).join('\n\n');
      existing.preview = existing.content.slice(0, 120).replace(/\n/g, ' ') + (existing.content.length > 120 ? '…' : '');
    } else {
      byNormalizedTitle.set(key, { ...s });
    }
  }
  filtered = Array.from(byNormalizedTitle.values()).filter((s) => s.content.trim().length > 0);

  // If first section is "Resume" and mixes contact + summary, split so contact card gets only contact lines
  if (filtered.length > 0 && filtered[0].title.toLowerCase() === 'resume') {
    const first = filtered[0];
    const firstLines = first.content.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const contactLines: string[] = [];
    const restLines: string[] = [];
    let seenNonContact = false;
    for (const ln of firstLines) {
      const looksLikeContact = /^[\w.-]+@[\w.-]+\.\w+/.test(ln) || /^(phone|tel|mobile|linkedin|email)\s*:?\s*/i.test(ln) || /^\+?\d[\d\s\-().]{8,}/.test(ln) || (ln.length < 60 && /linkedin\.com|github\.com|\.com\s*\|/i.test(ln)) || (ln.length <= 45 && /^[A-Z][a-z]+\s+[A-Z][a-z]+(\s+[A-Z][a-z]+)?$/.test(ln) && !/@|http|\.com|java|spring|python/i.test(ln));
      if (looksLikeContact && !seenNonContact) {
        contactLines.push(ln);
      } else {
        seenNonContact = true;
        restLines.push(ln);
      }
    }
    if (contactLines.length >= 1 && restLines.length >= 1) {
      const contactContent = contactLines.join('\n');
      const summaryContent = restLines.join('\n');
      filtered = [
        { title: 'Contact', content: contactContent, preview: contactContent.slice(0, 120).replace(/\n/g, ' ') + (contactContent.length > 120 ? '…' : '') },
        { title: 'Professional Summary', content: summaryContent, preview: summaryContent.slice(0, 120).replace(/\n/g, ' ') + (summaryContent.length > 120 ? '…' : '') },
        ...filtered.slice(1),
      ];
    }
  }

  if (filtered.length === 0 && fullText.trim()) {
    return [{ title: 'Resume', content: fullText.trim(), preview: fullText.slice(0, 100).replace(/\n/g, ' ') + (fullText.length > 100 ? '…' : '') }];
  }
  console.log('[splitResumeByDocumentHeaders] Document sections (exact titles):', filtered.map((s) => `"${s.title}" (${s.content.split(/\n/).filter((l) => l.trim()).length} lines)`).join(', '));
  return filtered;
}

/** Canonical section title for display (title case, consistent). */
const CANONICAL_TITLES: Record<string, string> = {
  'resume': 'Resume',
  'full resume': 'Full Resume',
  'personal information': 'Personal Information',
  'personal details': 'Personal Details',
  'contact information': 'Contact Information',
  'contact details': 'Contact Details',
  'contact': 'Contact',
  'professional summary': 'Professional Summary',
  'executive summary': 'Executive Summary',
  'career summary': 'Career Summary',
  'summary of qualifications': 'Summary of Qualifications',
  'summary': 'Summary',
  'profile': 'Profile',
  'objective': 'Objective',
  'career objective': 'Career Objective',
  'work experience': 'Work Experience',
  'experience': 'Work Experience',
  'employment': 'Work Experience',
  'employment history': 'Employment History',
  'work history': 'Work History',
  'professional experience': 'Professional Experience',
  'career history': 'Career History',
  'education': 'Education',
  'academic background': 'Academic Background',
  'qualifications': 'Qualifications',
  'technical skills': 'Technical Skills',
  'technical proficiencies': 'Technical Proficiencies',
  'technical expertise': 'Technical Expertise',
  'skills': 'Skills',
  'key skills': 'Key Skills',
  'languages': 'Languages',
  'languages & frameworks': 'Languages & Frameworks',
  'foreign languages': 'Foreign Languages',
  'frameworks': 'Frameworks',
  'certifications': 'Certifications',
  'certificates': 'Certifications',
  'awards': 'Awards',
  'honors': 'Awards',
  'awards & honors': 'Awards & Honors',
  'achievements': 'Achievements',
  'key achievements': 'Key Achievements',
  'projects': 'Projects',
  'references': 'References',
  'reference': 'References',
  'expertise': 'Expertise',
  'product management & design tools': 'Product Management & Design Tools',
  'strengths': 'Strengths',
  'hobbies': 'Hobbies',
  'publications': 'Publications',
  'volunteer experience': 'Volunteer Experience',
  'professional development': 'Professional Development',
  'training': 'Training',
  'additional information': 'Additional Information',
};
function toCanonicalTitle(title: string): string {
  const key = title.toLowerCase().trim();
  return CANONICAL_TITLES[key] || title.replace(/\b\w/g, c => c.toUpperCase());
}

/** Classify a content line by type so we can reassign misplaced content. */
function classifyContentLine(line: string): 'language' | 'certification' | 'education' | 'work_award' | 'other' {
  const t = line.trim();
  const lower = t.toLowerCase();
  if (!t) return 'other';
  // Language: "English (Basic)", "Spanish", "Fluent in X", "Native", language names + proficiency
  const languagePattern = /\b(english|spanish|portuguese|french|german|hindi|chinese|japanese|arabic|dutch|italian|russian|korean|basic|fluent|native|proficient|conversational)\b/i;
  const looksLikeProficiency = /\(basic\)|\(fluent\)|\(native\)|\(proficient\)|\(intermediate\)|\(advanced\)/i.test(lower);
  if (languagePattern.test(lower) && (looksLikeProficiency || t.length < 50 || /^[\w\s(),\-]+$/.test(t))) {
    return 'language';
  }
  // Certification: Certified, SCJP, NanoDegree, Certification, Program (
  if (/\b(certified|certification|scjp|nano\s*degree|nanodegree|exam|credential|program\s*\(|course\s*\))/i.test(lower)) {
    return 'certification';
  }
  // Education: ISB, University, College, Sep 2024, 2020, B\.Tech, MBA, degree
  if (/\b(university|college|institute|isb|iim|iit|b\.tech|mba|msc|bsc|ba\b|ma\b|degree|graduat)/i.test(lower)) {
    return 'education';
  }
  if (/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s*\d{4}/i.test(t) && (lower.includes('%') || lower.includes('product') || lower.includes('management'))) {
    return 'education';
  }
  // Work / Award: company names, Won, Award, Stellar, contribution, deliveries
  if (/\b(won|award|stellar|contribution|deliveries|tech mahindra|netcracker|at&t|british telecom)\b/i.test(lower)) {
    return 'work_award';
  }
  if (/^[•*\-]\s*.+/.test(t) && t.length > 30) return 'work_award';
  return 'other';
}

/**
 * Smart post-process: merge duplicate sections, reassign misplaced content (e.g. certifications
 * and education out of "Languages"), and normalize section titles.
 */
function smartPostProcessSections(sections: ResumeSection[]): ResumeSection[] {
  // 1) Merge sections with same normalized title (e.g. "languages" + "Languages")
  const byKey = new Map<string, ResumeSection>();
  for (const s of sections) {
    const key = s.title.toLowerCase().trim();
    const canonical = toCanonicalTitle(s.title);
    const existing = byKey.get(key);
    if (existing) {
      existing.content = [existing.content, s.content].filter(Boolean).join('\n\n');
      existing.preview = existing.content.slice(0, 100).replace(/\n/g, ' ') + (existing.content.length > 100 ? '…' : '');
    } else {
      byKey.set(key, { title: canonical, content: s.content, preview: s.preview });
    }
  }

  // 1b) Second dedup pass by canonical title: catches aliases like "honors" → "Awards" and
  //     "awards" → "Awards" that survive step 1 because they have different lowercase keys.
  const byCanonical = new Map<string, ResumeSection>();
  for (const s of byKey.values()) {
    const canonKey = s.title.toLowerCase().trim();
    const existing = byCanonical.get(canonKey);
    if (existing) {
      existing.content = [existing.content, s.content].filter(Boolean).join('\n\n');
      existing.preview = existing.content.slice(0, 100).replace(/\n/g, ' ') + (existing.content.length > 100 ? '…' : '');
    } else {
      byCanonical.set(canonKey, { ...s });
    }
  }

  let list = Array.from(byCanonical.values()).filter(s => s.content.length > 0);

  // 2) Reassign misplaced content in "Languages" and "Reference"
  const certificationsLines: string[] = [];
  const educationLines: string[] = [];
  const workAwardLines: string[] = [];
  const languageSections: ResumeSection[] = [];
  const otherSections: ResumeSection[] = [];

  for (const section of list) {
    const key = section.title.toLowerCase().trim();
    if (key === 'languages' || key === 'languages & frameworks') {
      const lines = section.content.split(/\r?\n/).filter(l => l.trim());
      const languageLines: string[] = [];
      for (const line of lines) {
        const kind = classifyContentLine(line);
        if (kind === 'language') languageLines.push(line);
        else if (kind === 'certification') certificationsLines.push(line);
        else if (kind === 'education') educationLines.push(line);
        else if (kind === 'work_award') workAwardLines.push(line);
        else languageLines.push(line);
      }
      const newContent = languageLines.join('\n').trim();
      if (newContent) {
        languageSections.push({
          title: key === 'languages & frameworks' ? 'Languages & Frameworks' : 'Languages',
          content: newContent,
          preview: newContent.slice(0, 100).replace(/\n/g, ' ') + (newContent.length > 100 ? '…' : '')
        });
      }
      continue;
    }
    if (key === 'reference' || key === 'references') {
      const lines = section.content.split(/\r?\n/).filter(l => l.trim());
      const allLanguageLike = lines.length > 0 && lines.every(l => classifyContentLine(l) === 'language');
      if (allLanguageLike) {
        languageSections.push({
          title: 'Languages',
          content: section.content,
          preview: section.preview
        });
      } else {
        otherSections.push(section);
      }
      continue;
    }
    otherSections.push(section);
  }

  // 3) Rebuild list: other sections first, then merge in extracted Certifications/Education/Awards, then Languages
  const result: ResumeSection[] = [];
  for (const s of otherSections) {
    const norm = s.title.toLowerCase().trim();
    if (norm === 'languages' || norm === 'languages & frameworks') continue;
    result.push({ title: toCanonicalTitle(s.title), content: s.content, preview: s.preview });
  }

  const appendOrAdd = (canonicalTitle: string, lines: string[]) => {
    if (lines.length === 0) return;
    const content = lines.join('\n');
    const existing = result.find(r => r.title.toLowerCase() === canonicalTitle.toLowerCase());
    if (existing) {
      existing.content = [existing.content, content].filter(Boolean).join('\n\n');
      existing.preview = existing.content.slice(0, 100).replace(/\n/g, ' ') + (existing.content.length > 100 ? '…' : '');
    } else {
      result.push({
        title: canonicalTitle,
        content,
        preview: content.slice(0, 100).replace(/\n/g, ' ') + (content.length > 100 ? '…' : '')
      });
    }
  };
  appendOrAdd('Certifications', certificationsLines);
  appendOrAdd('Education', educationLines);
  appendOrAdd('Awards', workAwardLines);

  for (const s of languageSections) {
    const norm = s.title.toLowerCase().trim();
    const existing = result.find(r => r.title.toLowerCase() === norm || (norm === 'languages' && r.title.toLowerCase() === 'languages'));
    if (existing) {
      existing.content = [existing.content, s.content].filter(Boolean).join('\n\n');
      existing.preview = existing.content.slice(0, 100).replace(/\n/g, ' ') + (existing.content.length > 100 ? '…' : '');
    } else {
      result.push(s);
    }
  }

  return result.filter(s => s.content.length > 0);
}

/** Same order as CANONICAL_SECTION_BUCKETS so Work Experience, Skills, etc. map correctly. */
const STRUCTURED_SECTION_ORDER = [
  'Personal Information', 'Contact', 'Contact Information',
  'Professional Summary', 'Summary', 'Profile', 'Objective',
  'Work Experience', 'Experience', 'Employment', 'Professional Experience',
  'Education',
  'Skills', 'Technical Proficiencies', 'Technical Skills',
  'Languages & Frameworks', 'Languages',
  'Certifications', 'Certificates',
  'Awards', 'Awards & Honors', 'Honors',
  'Projects',
  'Publications', 'Research',
  'Training & Development', 'Leadership & Activities',
  'Affiliations', 'Hobbies & Interests',
  'Additional Information', 'References',
];

/**
 * Convert AI/LangChain structured resume (Record<string, Array<{text?: string} | string>>) to ResumeSection[].
 * Use this so the Review step shows AI-parsed sections instead of heuristic text parsing when available.
 */
export function structuredResumeToSections(structured: Record<string, unknown[]> | null | undefined): ResumeSection[] {
  if (!structured || typeof structured !== 'object') return [];
  const result: ResumeSection[] = [];
  const seen = new Set<string>();
  const pushSection = (title: string, content: string) => {
    if (!content.trim()) return;
    const key = title.toLowerCase().trim();
    if (seen.has(key)) return;
    seen.add(key);
    result.push({
      title,
      content: content.trim(),
      preview: content.trim().slice(0, 120).replace(/\n/g, ' ') + (content.trim().length > 120 ? '…' : '')
    });
  };
  const getText = (item: unknown): string => {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item !== null && 'text' in item && typeof (item as { text: unknown }).text === 'string') return (item as { text: string }).text;
    return String(item);
  };
  // First pass: known order
  for (const key of STRUCTURED_SECTION_ORDER) {
    if (!(key in structured)) continue;
    const arr = structured[key];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const lines = arr.map(getText).filter(Boolean);
    if (lines.length) pushSection(key, lines.join('\n'));
  }
  // Second pass: any other keys (e.g. custom section names from LLM)
  for (const key of Object.keys(structured)) {
    if (STRUCTURED_SECTION_ORDER.some(k => k.toLowerCase() === key.toLowerCase())) continue;
    const arr = structured[key];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const lines = arr.map(getText).filter(Boolean);
    if (lines.length) pushSection(key, lines.join('\n'));
  }
  return result;
}

/** Sub-header line patterns: line ending or full match → treat as new sub-section when splitting. */
const SUB_HEADER_ENDINGS = [
  ' tools', ' technologies', ' methods', ' frameworks', ' algorithms', ' software',
  ' web services', ' design tools', ' proficiencies', ' competencies', ' skills'
];
/** Top-level section words: do NOT split at these (they would fragment Work Experience etc.). */
const TOP_LEVEL_SECTION_WORDS = new Set([
  'experience', 'technologies', 'technology', 'expertise', 'skills', 'education', 'awards',
  'certifications', 'references', 'languages', 'frameworks', 'software', 'summary', 'profile',
  'objective', 'contact', 'employment', 'projects', 'honors', 'achievements'
]);
function looksLikeSubHeader(line: string): boolean {
  const t = line.trim();
  if (t.length < 12 || t.length > 55) return false;
  if (/^[•*\-]\s/.test(t)) return false;
  const lower = t.toLowerCase();
  if (TOP_LEVEL_SECTION_WORDS.has(lower)) return false;
  if (SECTION_HEADER_NAMES.some((n) => lower === n || lower.startsWith(n + ' ') || lower.startsWith(n + ':'))) return false;
  return SUB_HEADER_ENDINGS.some((end) => lower.endsWith(end));
}

/**
 * Normalize sections from any source (document parser, AI, heuristic): merge duplicate/Languages,
 * split large blocks by sub-header lines (e.g. "Product Management Tools") so content isn't one blob.
 */
export function normalizeResumeSections(sections: ResumeSection[]): ResumeSection[] {
  if (!sections?.length) return sections;

  // 1) Merge by normalized title
  const byKey = new Map<string, ResumeSection>();
  for (const s of sections) {
    const key = s.title.toLowerCase().trim();
    const existing = byKey.get(key);
    if (existing) {
      existing.content = [existing.content, s.content].filter(Boolean).join('\n\n');
      existing.preview = existing.content.slice(0, 120).replace(/\n/g, ' ') + (existing.content.length > 120 ? '…' : '');
    } else {
      byKey.set(key, { title: s.title.trim(), content: s.content, preview: s.preview });
    }
  }

  // 2) Merge "Languages" into "Languages & Frameworks" ONLY when the Languages section is a
  //    parsing artifact (its content is just a "&" or "& Frameworks" fragment, not real language data).
  //    If "Languages" has actual human-language entries (English, Spanish, etc.) keep it separate.
  const langKey = 'languages';
  const langFwKey = 'languages & frameworks';
  const langSection = byKey.get(langKey);
  const langFwSection = byKey.get(langFwKey);
  if (langSection || langFwSection) {
    const HUMAN_LANG_NAMES = /\b(english|spanish|portuguese|french|german|hindi|chinese|japanese|arabic|dutch|italian|russian|korean|norwegian|swedish|danish|polish|turkish|malay|tagalog|urdu|bengali|tamil|telugu)\b/i;
    const langContent = langSection?.content.trim() ?? '';
    const isFragmentOnly = !langContent ||
      /^[\s&\-]+$/.test(langContent) ||
      /^&\s*Frameworks?$/i.test(langContent) ||
      (langContent.startsWith('& ') && langContent.length < 20);
    const hasRealHumanLanguages = !isFragmentOnly && HUMAN_LANG_NAMES.test(langContent);

    if (isFragmentOnly) {
      // Parsing artifact: "Languages" had content like "& Frameworks" — merge into Languages & Frameworks
      byKey.delete(langKey);
      byKey.delete(langFwKey);
      const parts: string[] = [];
      const addContent = (content: string) => {
        const trimmed = content.trim();
        if (!trimmed) return;
        if (/^[\s&\-]+$/.test(trimmed) || /^&\s*Frameworks?$/i.test(trimmed)) return;
        parts.push(trimmed);
      };
      if (langSection) addContent(langSection.content);
      if (langFwSection) addContent(langFwSection.content);
      const mergedContent = parts.join('\n\n');
      if (mergedContent) {
        byKey.set(langFwKey, {
          title: 'Languages & Frameworks',
          content: mergedContent,
          preview: mergedContent.slice(0, 120).replace(/\n/g, ' ') + (mergedContent.length > 120 ? '…' : ''),
        });
      }
    } else if (hasRealHumanLanguages && langFwSection) {
      // Keep both sections separate: human languages stay in "Languages", tech content in "Languages & Frameworks"
      // No merging needed — bucketResumeSections will correctly split them later
    }
    // else: only one section exists and it has real content — leave as-is
  }

  let list = Array.from(byKey.values()).filter((s) => s.content.length > 0 || s.title.length > 0);

  // 3) Split sections with long content by sub-header lines
  const out: ResumeSection[] = [];
  for (const section of list) {
    const lines = section.content.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 4 || section.content.length < 200) {
      out.push(section);
      continue;
    }
    const chunks: { title: string; lines: string[] }[] = [];
    let currentTitle = section.title;
    let currentLines: string[] = [];
    for (const line of lines) {
      if (looksLikeSubHeader(line)) {
        if (currentLines.length) {
          chunks.push({ title: currentTitle, lines: [...currentLines] });
        }
        currentTitle = line.trim();
        currentLines = [];
      } else {
        currentLines.push(line);
      }
    }
    if (currentLines.length) chunks.push({ title: currentTitle, lines: currentLines });

    if (chunks.length <= 1) {
      out.push(section);
    } else {
      for (const { title, lines: ls } of chunks) {
        const content = ls.join('\n').trim();
        if (content) {
          out.push({
            title,
            content,
            preview: content.slice(0, 120).replace(/\n/g, ' ') + (content.length > 120 ? '…' : ''),
          });
        }
      }
    }
  }

  return out.filter((s) => s.content.length > 0 || s.title.length > 0);
}

/** Canonical section buckets in display order. Every section is assigned to exactly one. */
export const CANONICAL_SECTION_BUCKETS = [
  'Personal Information',
  'Professional Summary',
  'Work Experience',
  'Education',
  'Skills',
  'Languages',
  'Languages & Frameworks',
  'Certifications',
  'Awards',
  'Projects',
  'Publications',
  'Research',
  'Training & Development',
  'Leadership & Activities',
  'Affiliations',
  'Hobbies & Interests',
  'Additional Information',
  'References',
] as const;

/** Index of each canonical bucket for sorting. */
const CANONICAL_ORDER_INDEX = new Map<string, number>(
  CANONICAL_SECTION_BUCKETS.map((b, i) => [b.toLowerCase(), i])
);

/**
 * Sort sections into the canonical display order (Personal Info → Summary → Work Experience → Education → Skills → …).
 * Sections not in CANONICAL_SECTION_BUCKETS go at the end, preserving their relative order.
 */
export function sortSectionsByCanonicalOrder<T extends { title: string }>(sections: T[]): T[] {
  if (!sections?.length) return sections;
  const getOrder = (title: string): number => {
    const bucket = resolveBucket(title);
    if (bucket) return CANONICAL_ORDER_INDEX.get(bucket.toLowerCase()) ?? 999;
    return 999;
  };
  return [...sections].sort((a, b) => getOrder(a.title) - getOrder(b.title));
}

/** Map section titles (and common variants) to canonical bucket name. Every captured section must map so content lands in the right place. */
const TITLE_TO_BUCKET: Record<string, (typeof CANONICAL_SECTION_BUCKETS)[number]> = {
  'personal information': 'Personal Information',
  'personal details': 'Personal Information',
  'contact': 'Personal Information',
  'contact information': 'Personal Information',
  'contact details': 'Personal Information',
  'resume': 'Personal Information',
  'professional summary': 'Professional Summary',
  'executive summary': 'Professional Summary',
  'career summary': 'Professional Summary',
  'summary of qualifications': 'Professional Summary',
  'summary': 'Professional Summary',
  'profile': 'Professional Summary',
  'objective': 'Professional Summary',
  'career objective': 'Professional Summary',
  'work experience': 'Work Experience',
  'experience': 'Work Experience',
  'employment': 'Work Experience',
  'employment history': 'Work Experience',
  'work history': 'Work Experience',
  'professional experience': 'Work Experience',
  'career history': 'Work Experience',
  'professional history': 'Work Experience',
  'education': 'Education',
  'professional education': 'Education',
  'professional education and certification': 'Education',
  'professional education and certifications': 'Education',
  'education and certification': 'Education',
  'education and certifications': 'Education',
  'academic background': 'Education',
  'academic qualifications': 'Education',
  'qualifications': 'Education',
  'academic experience': 'Education',
  'teaching experience': 'Education',
  'skills': 'Skills',
  'key skills': 'Skills',
  'technical skills': 'Skills',
  'technical proficiencies': 'Skills',
  'technical expertise': 'Skills',
  'expertise': 'Skills',
  'technologies': 'Skills',
  'domain expertise': 'Skills',
  'core competencies': 'Skills',
  'competencies': 'Skills',
  'product management & design tools': 'Skills',
  'product management tools': 'Skills',
  'software': 'Skills',
  'methodologies': 'Skills',
  'tools': 'Skills',
  'ml & data science': 'Skills',
  'ai/ml & data science': 'Skills',
  'cloud & devops technologies': 'Skills',
  'cloud & devops': 'Skills',
  'languages': 'Languages',
  'foreign languages': 'Languages',
  'spoken languages': 'Languages',
  'language proficiency': 'Languages',
  'language skills': 'Languages',
  'languages & frameworks': 'Languages & Frameworks',
  'programming languages': 'Languages & Frameworks',
  'technical languages': 'Languages & Frameworks',
  'frameworks': 'Languages & Frameworks',
  'certifications': 'Certifications',
  'certificates': 'Certifications',
  'professional certifications': 'Certifications',
  'awards': 'Awards',
  'honors': 'Awards',
  'awards & honors': 'Awards',
  'recognition': 'Awards',
  'achievements': 'Awards',
  'key achievements': 'Awards',
  'career achievements': 'Awards',
  'projects': 'Projects',
  'key projects': 'Projects',
  'side projects': 'Projects',
  'open source': 'Projects',
  'volunteer experience': 'Projects',
  'volunteer work': 'Projects',
  'community service': 'Projects',
  'additional information': 'Additional Information',
  'additional details': 'Additional Information',
  'references': 'References',
  'reference': 'References',
  'hobbies': 'Hobbies & Interests',
  'hobbies & interests': 'Hobbies & Interests',
  'interests': 'Hobbies & Interests',
  'personal interests': 'Hobbies & Interests',
  'strengths': 'Skills',
  'core strengths': 'Skills',
  'publications': 'Publications',
  'publication': 'Publications',
  'papers': 'Publications',
  'journal articles': 'Publications',
  'patents': 'Publications',
  'research': 'Research',
  'research experience': 'Research',
  'research & publications': 'Research',
  'professional development': 'Training & Development',
  'training': 'Training & Development',
  'training & development': 'Training & Development',
  'courses': 'Training & Development',
  'continuing education': 'Training & Development',
  'professional training': 'Training & Development',
  'workshops': 'Training & Development',
  'leadership': 'Leadership & Activities',
  'leadership experience': 'Leadership & Activities',
  'activities': 'Leadership & Activities',
  'extracurricular': 'Leadership & Activities',
  'extracurricular activities': 'Leadership & Activities',
  'affiliations': 'Affiliations',
  'professional affiliations': 'Affiliations',
  'memberships': 'Affiliations',
  'professional memberships': 'Affiliations',
  'market strategy': 'Skills',
  'goal': 'Professional Summary',
  // Additional common section title variants
  'about me': 'Professional Summary',
  'about': 'Professional Summary',
  'personal statement': 'Professional Summary',
  'career profile': 'Professional Summary',
  'professional profile': 'Professional Summary',
  'professional background': 'Professional Summary',
  'background': 'Professional Summary',
  'professional overview': 'Professional Summary',
  'overview': 'Professional Summary',
  'introduction': 'Professional Summary',
  'bio': 'Professional Summary',
  'it skills': 'Skills',
  'professional skills': 'Skills',
  'functional skills': 'Skills',
  'key competencies': 'Skills',
  'technical proficiency': 'Skills',
  'skill set': 'Skills',
  'skillset': 'Skills',
  'areas of expertise': 'Skills',
  'area of expertise': 'Skills',
  'professional competencies': 'Skills',
  'digital skills': 'Skills',
  'management skills': 'Skills',
  'soft skills': 'Skills',
  'hard skills': 'Skills',
  'internship': 'Work Experience',
  'internship experience': 'Work Experience',
  'internships': 'Work Experience',
  'work': 'Work Experience',
  'positions': 'Work Experience',
  'positions held': 'Work Experience',
  'relevant experience': 'Work Experience',
  'key responsibilities': 'Work Experience',
  'responsibilities': 'Work Experience',
  'role': 'Work Experience',
  'roles': 'Work Experience',
  'accomplishments': 'Awards',
  'key accomplishments': 'Awards',
  'notable achievements': 'Awards',
  'professional achievements': 'Awards',
  'highlights': 'Awards',
  'academic projects': 'Projects',
  'personal projects': 'Projects',
  'portfolio': 'Projects',
  'social activities': 'Leadership & Activities',
  'personal information and interests': 'Hobbies & Interests',
  'personal interests and hobbies': 'Hobbies & Interests',
};

function resolveBucket(sectionTitle: string): (typeof CANONICAL_SECTION_BUCKETS)[number] | null {
  const key = sectionTitle.toLowerCase().trim();
  if (TITLE_TO_BUCKET[key]) return TITLE_TO_BUCKET[key];
  for (const [title, bucket] of Object.entries(TITLE_TO_BUCKET)) {
    if (key === title || key.startsWith(title + ' ') || key.startsWith(title + ':')) return bucket;
  }
  if (key.endsWith(' tools') || key.endsWith(' technologies') || key.endsWith(' skills')) return 'Skills';
  return null;
}

/**
 * Renames each section's title to its canonical bucket name (e.g. "Summary" →
 * "Professional Summary", "Experience" → "Work Experience") WITHOUT touching
 * or reclassifying the section content.
 *
 * Use this in the PDF/DOCX generator before section lookups so that
 * `find(s => s.title === bucket)` reliably finds all sections regardless of
 * how the LLM or parser spelled the title.
 */
export function normalizeSectionTitles(sections: ResumeSection[]): ResumeSection[] {
  return sections.map(s => {
    const canonical = resolveBucket(s.title);
    return canonical ? { ...s, title: canonical } : s;
  });
}

/**
 * Put every section into the correct canonical bucket. Merges content that belongs to the same bucket,
 * reassigns misplaced lines (e.g. certs in Languages → Certifications), and returns sections in bucket order.
 */
export function bucketResumeSections(sections: ResumeSection[]): ResumeSection[] {
  if (!sections?.length) return sections;

  console.group('[bucketResumeSections] INPUT SECTIONS (before putting in buckets)');
  sections.forEach((s) => {
    console.log(`--- [${s.title}] (${s.content.split(/\r?\n/).filter((l) => l.trim()).length} lines, ${s.content.length} chars) ---`);
    console.log(s.content);
  });
  console.groupEnd();

  console.group('[bucketResumeSections] Putting resume text in proper sections/buckets');
  const bucketContent = new Map<string, string[]>();

  const appendToBucket = (bucket: string, content: string) => {
    const trimmed = content.trim();
    if (!trimmed) return;
    const list = bucketContent.get(bucket) || [];
    list.push(trimmed);
    bucketContent.set(bucket, list);
  };

  for (const section of sections) {
    const bucket = resolveBucket(section.title) || 'Additional Information';
    const key = section.title.toLowerCase().trim();
    const lineCount = section.content.split(/\r?\n/).filter((l) => l.trim()).length;
    console.log(`  Section "${section.title}" → bucket [${bucket}] (${lineCount} lines)`);

    if (key === 'languages' || key === 'languages & frameworks' || key === 'foreign languages' || key === 'spoken languages' || key === 'language proficiency' || key === 'language skills') {
      const lines = section.content.split(/\r?\n/).filter((l) => l.trim());
      const HUMAN_LANG_RE = /\b(english|spanish|portuguese|french|german|hindi|chinese|japanese|arabic|dutch|italian|russian|korean|norwegian|swedish|danish|polish|turkish|malay|tagalog|urdu|bengali|tamil|telugu)\b/gi;
      const PROG_LANG_RE = /\b(java|python|javascript|typescript|go|golang|kotlin|swift|ruby|php|c\+\+|c#|r\b|scala|rust|bash|shell|html|css|sql|nosql|graphql|perl|matlab)\b/i;
      // Orphan subsection-header fragments that should be dropped (e.g. "Cloud" from "Cloud Technologies" split)
      const SUBSECTION_FRAGMENT_RE = /^(cloud\s*$|technical\s*$|frameworks?\s*$|technologies?\s*$|tools\s*$|skills\s*$|and\s*$|&\s*$)/i;
      const humanLines: string[] = [];
      const techLines: string[] = [];
      for (const line of lines) {
        const t = line.trim();
        // Skip orphan subsection header labels (parsing artifacts from split headers)
        if (SUBSECTION_FRAGMENT_RE.test(t) && t.length <= 20) continue;
        const kind = classifyContentLine(line);
        if (kind === 'certification') { appendToBucket('Certifications', line); continue; }
        if (kind === 'education') { appendToBucket('Education', line); continue; }
        if (kind === 'work_award') { appendToBucket('Awards', line); continue; }
        // Route human vs programming languages
        const hasHuman = HUMAN_LANG_RE.test(line);
        const hasProg = PROG_LANG_RE.test(line);
        if (hasProg && !hasHuman) {
          // Clearly a programming/framework line
          techLines.push(line);
        } else if (hasHuman && !hasProg) {
          // Clearly a spoken-language line
          humanLines.push(line);
        } else if (key === 'languages & frameworks') {
          // Mixed or ambiguous in a tech-oriented section → tech
          techLines.push(line);
        } else {
          // Plain 'languages' section → default to human
          humanLines.push(line);
        }
      }
      if (humanLines.length) appendToBucket('Languages', humanLines.join('\n'));
      if (techLines.length) appendToBucket('Languages & Frameworks', techLines.join('\n'));
      continue;
    }

    if (key === 'certifications' || key === 'certificates') {
      appendToBucket('Certifications', section.content);
      continue;
    }

    // For Skills bucket: strip orphan section-header fragments before appending
    if (bucket === 'Skills') {
      const SKILLS_ORPHAN_RE = /^(languages\s*&?\s*$|ai\s*\/?\s*ml\s*$|cloud\s*$|professional\s*$|technical\s*$|frameworks?\s*$|technologies?\s*$|tools\s*$|certifications?\s*$|education\s*$|experience\s*$|summary\s*$)/i;
      const cleanedLines = section.content
        .split(/\r?\n/)
        .filter((l) => {
          const t = l.trim();
          return t && !(SKILLS_ORPHAN_RE.test(t) && t.length <= 25);
        })
        .join('\n');
      if (cleanedLines.trim()) appendToBucket(bucket, cleanedLines);
      continue;
    }

    appendToBucket(bucket, section.content);
  }

  const result: ResumeSection[] = [];
  for (const bucket of CANONICAL_SECTION_BUCKETS) {
    const parts = bucketContent.get(bucket);
    if (!parts?.length) continue;
    const content = parts.join('\n\n').trim();
    if (!content) continue;
    result.push({
      title: bucket,
      content,
      preview: content.slice(0, 120).replace(/\n/g, ' ') + (content.length > 120 ? '…' : ''),
    });
  }

  const other = Array.from(bucketContent.entries()).filter(
    ([b]) => !CANONICAL_SECTION_BUCKETS.includes(b as (typeof CANONICAL_SECTION_BUCKETS)[number])
  );
  for (const [bucket, parts] of other) {
    const content = parts.join('\n\n').trim();
    if (content) result.push({ title: bucket, content, preview: content.slice(0, 120).replace(/\n/g, ' ') + (content.length > 120 ? '…' : '') });
  }

  console.log('  Final buckets with content:', result.map((s) => s.title).join(', '));
  console.groupEnd();
  return redistributeMisplacedContent(result);
}

type BucketName = (typeof CANONICAL_SECTION_BUCKETS)[number];

/**
 * After bucketing: move misplaced content so cards show resume as-is in the right place.
 * - Professional Summary: move skill blocks (Languages & Frameworks:, Cloud & DevOps, AI/ML, etc.) and award lines to Skills/Awards
 * - Certifications: move education lines (ISB, MBA, degree, %) to Education
 * - Skills: move spoken-language-only lines (English, Spanish, etc.) to Languages & Frameworks
 */
/** True if line looks like a full name (e.g. "Dharmendra Singh", "John Doe"). */
function looksLikeName(line: string): boolean {
  const t = line.trim();
  if (t.length < 3 || t.length > 55) return false;
  if (/@|http|\.com|linkedin|phone|email|\d{5}|^\s*$/.test(t)) return false;
  if (/[|•\-–—]\s*[A-Z]/.test(t)) return false;
  if (/\b(manager|engineer|designer|developer|analyst|consultant|director|lead|intern|specialist|officer|architect|scientist|productboard|figma|miro)\b/i.test(t)) return false;
  const parts = t.split(/\s+/);
  if (parts.length < 1 || parts.length > 5) return false;
  return parts.every((p) => /^[A-Z][a-z]+$/.test(p) || /^[A-Z][a-z]*\.?$/.test(p) || /^[A-Z][a-z]+-[A-Z][a-z]+$/.test(p));
}

/** True if line is contact info: email, phone, LinkedIn, location, or combined. */
function looksLikeContactLine(line: string): boolean {
  const t = line.trim();
  if (looksLikeToolListLine(t) || isJobHistoryLine(t)) return false;
  if (/^[\w.-]+@[\w.-]+\.\w+/.test(t)) return true;
  if (/^(email|e-mail)\s*:?\s*[\w.-]+@[\w.-]+\.\w+/i.test(t)) return true;
  if (/^(\+\d[\d\s\-().]{8,}|phone|tel|mobile)\s*:?\s*/i.test(t)) return true;
  if (/^(phone|tel|mobile)\s*:?\s*\+?\d[\d\s\-().]{6,}/i.test(t)) return true;
  if (/linkedin\.com|linkedin\s*:/i.test(t)) return true;
  if (/github\.com|portfolio|behance\.net|dribbble\.com/i.test(t) && t.length < 120 && !DESIGN_OR_TOOL_RE.test(t)) return true;
  if (/^[^|]+\|[^|]+\|/.test(t) && (t.includes('@') || t.includes('+') || t.includes('linkedin'))) return true;
  // Only treat as a location-only contact line when there are no year dates (which would indicate a job entry)
  const hasYearDate = /\b\d{4}\b/.test(t);
  const hasCompanyIndicator = /\b(pvt|ltd|inc|llc|technologies|solutions|consulting|systems|mahindra|infosys|wipro|tcs|accenture|capgemini|ibm|cognizant|oracle|cisco)\b/i.test(t);
  if (!hasYearDate && !hasCompanyIndicator && t.length < 80 && /^\s*(bengaluru|pune|mumbai|hyderabad|bangalore|india|usa|uk|london|new\s+york|city\s*,?\s*[A-Z]{2}\s*\d+)/i.test(t)) return true;
  return false;
}

function redistributeMisplacedContent(sections: ResumeSection[]): ResumeSection[] {
  const bucketLines = new Map<string, string[]>();
  for (const s of sections) {
    const lines = s.content.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    bucketLines.set(s.title, lines);
  }

  // 0a) Personal Information: evict non-contact lines that leaked in during initial parsing
  //     (e.g. job entries, skills bullets, summary phrases captured before the first named header).
  const piLines = bucketLines.get('Personal Information');
  if (piLines?.length) {
    const keepPI: string[] = [];
    const toSummaryFromPI: string[] = [];
    const toExpFromPI: string[] = [];
    const toSkillsFromPI: string[] = [];
    const JOB_ENTRY_RE = /\b\d{2}\/\d{4}\b|\b\d{4}\s*[-–]\s*(till\s*date|present|\d{4})/i;
    const SKILLS_LABEL_RE = /^(telecom\s+bss|oss\b|core\s+platform|poc\b|product\s+management|designing\s+product|portfolio\s+and|safe\s+adoption|analysing\s+product)/i;
    const SUMMARY_PHRASE_RE = /\b(years?\s+of\s+experience|experienced\s+(in|with)|product\s+manager|technical\s+manager|senior\s+manager)\b/i;
    for (const line of piLines) {
      const t = line.trim();
      if (looksLikeContactLine(t) || looksLikeName(t)) {
        keepPI.push(line);
      } else if (JOB_ENTRY_RE.test(t) || isJobHistoryLine(t)) {
        toExpFromPI.push(line);
      } else if (looksLikeToolListLine(t) || SKILLS_LABEL_RE.test(t) || DESIGN_OR_TOOL_RE.test(t)) {
        toSkillsFromPI.push(line);
      } else if (SUMMARY_PHRASE_RE.test(t) && t.length > 30) {
        toSummaryFromPI.push(line);
      } else if (t.length > 40 && /[a-z]/.test(t)) {
        toSummaryFromPI.push(line);
      } else if (t.length > 0) {
        // Never keep leftover tokens (tools, fragments) in the contact card
        toSkillsFromPI.push(line);
      }
    }
    bucketLines.set('Personal Information', keepPI);
    if (toExpFromPI.length) {
      const existing = bucketLines.get('Work Experience') || [];
      bucketLines.set('Work Experience', [...existing, ...toExpFromPI]);
    }
    if (toSkillsFromPI.length) {
      const existing = bucketLines.get('Skills') || [];
      bucketLines.set('Skills', [...existing, ...toSkillsFromPI]);
    }
    if (toSummaryFromPI.length) {
      const existing = bucketLines.get('Professional Summary') || [];
      bucketLines.set('Professional Summary', [...existing, ...toSummaryFromPI]);
    }
  }

  // 0) Personal Information: pull name and ALL contact lines FROM Professional Summary so Contact card is complete
  const summaryLines = bucketLines.get('Professional Summary');
  if (summaryLines?.length) {
    const toContact: string[] = [];
    const restSummary: string[] = [];
    let nameMoved = false;
    for (const line of summaryLines) {
      if (looksLikeName(line) && !nameMoved) {
        toContact.push(line);
        nameMoved = true;
        continue;
      }
      if (looksLikeName(line) && nameMoved) {
        restSummary.push(line);
        continue;
      }
      if (looksLikeContactLine(line)) {
        toContact.push(line);
        continue;
      }
      restSummary.push(line);
    }
    if (toContact.length > 0) {
      const existingContact = bucketLines.get('Personal Information') || [];
      bucketLines.set('Personal Information', [...toContact, ...existingContact]);
      bucketLines.set('Professional Summary', restSummary.length ? restSummary : summaryLines);
    }
  }

  // Re-read summary after moving name/contact (for step 1 below)
  const summaryLinesAfterContact = bucketLines.get('Professional Summary') || [];

  // 0b) Professional Summary: job-dated blocks belong in Work Experience, never stay in Summary
  if (summaryLinesAfterContact.length) {
    const keepSummaryDates: string[] = [];
    const toExpFromSummary: string[] = [];
    const isSummaryProse = (line: string) =>
      /\b(i\s+am|i\s+have|years?\s+of\s+experience|experienced\s+(in|with)|seeking\s+to|passionate\s+about)\b/i.test(line)
      && !isJobHistoryLine(line);
    let si = 0;
    while (si < summaryLinesAfterContact.length) {
      const line = summaryLinesAfterContact[si];
      const next = summaryLinesAfterContact[si + 1] || '';
      const startsJob = isJobHistoryLine(line) || (isJobHistoryLine(next) && /[A-Z]/.test(line) && line.length < 90 && !isSummaryProse(line));
      if (startsJob) {
        toExpFromSummary.push(line);
        si++;
        while (si < summaryLinesAfterContact.length) {
          const block = summaryLinesAfterContact[si];
          if (isSummaryProse(block)) break;
          if (isJobHistoryLine(block) || isBulletLine(block) || block.length < 90) {
            toExpFromSummary.push(block);
            si++;
            continue;
          }
          break;
        }
        continue;
      }
      keepSummaryDates.push(line);
      si++;
    }
    if (toExpFromSummary.length) {
      bucketLines.set('Professional Summary', keepSummaryDates);
      const existingExp = bucketLines.get('Work Experience') || [];
      bucketLines.set('Work Experience', [...toExpFromSummary, ...existingExp]);
    }
  }

  const summaryLinesForSkills = bucketLines.get('Professional Summary') || [];

  // 1) Professional Summary: keep only real summary; move skill/award blocks to Skills and Awards
  if (summaryLinesForSkills.length > 6) {
    const keepSummary: string[] = [];
    const toSkills: string[] = [];
    const toAwards: string[] = [];
    const skillBlockStarts = /^(languages\s*&\s*frameworks|cloud\s*&\s*devops|ai\/ml|ai\s*&\s*ml|product\s*management\s*(&\s*(design\s*tools?|telecom|domain))?|technical\s*skills?|domain\s*expertise|core\s*competencies|methodologies|tools|product\s*management\s*&\s*telecom|telecom\s*domain)\s*:?\s*/i;
    const skillLineStart = /^(languages\s*&\s*frameworks|cloud\s*&\s*devops|ai\/ml|product\s*management(\s*&\s*(design|telecom))?|technical\s*skills?|domain\s*expertise|telecom\s*domain)\s*:?\s*/i;
    const awardStarts = /^(netcracker|tech\s*mahindra|won\s+multiple|stellar\s+success|award\s+for|contribution\s+in)/i;
    let i = 0;
    while (i < summaryLinesForSkills.length) {
      const line = summaryLinesForSkills[i];
      const lower = line.toLowerCase();
      if (skillBlockStarts.test(lower) || (line.match(/^[A-Za-z\s&]+:\s*/) && skillLineStart.test(lower))) {
        toSkills.push(line);
        i++;
        while (i < summaryLinesForSkills.length) {
          const next = summaryLinesForSkills[i];
          const nextLower = next.toLowerCase();
          if (awardStarts.test(nextLower)) break;
          if (skillBlockStarts.test(nextLower) || (next.match(/^[A-Za-z\s&]+:\s*/) && skillLineStart.test(nextLower))) break;
          // Stop absorbing into Skills when the line is a long descriptive paragraph (prose, not a skill item).
          // Long sentences with lowercase words mid-line belong in Professional Summary, not Skills.
          const wordCount = next.trim().split(/\s+/).length;
          const hasLowercaseMid = /[a-z]{3,}/.test(next.trim().slice(3));
          if (next.trim().length > 85 && wordCount > 10 && hasLowercaseMid) break;
          toSkills.push(next);
          i++;
        }
        continue;
      }
      if (awardStarts.test(lower) || (lower.includes('award') && lower.length < 120)) {
        toAwards.push(line);
        i++;
        while (i < summaryLinesForSkills.length && !skillBlockStarts.test(summaryLinesForSkills[i].toLowerCase())) {
          toAwards.push(summaryLinesForSkills[i]);
          i++;
        }
        continue;
      }
      keepSummary.push(line);
      i++;
    }
    bucketLines.set('Professional Summary', keepSummary.length > 0 ? keepSummary : summaryLinesForSkills);
    if (toSkills.length) {
      const existing = bucketLines.get('Skills') || [];
      bucketLines.set('Skills', [...existing, ...toSkills]);
    }
    if (toAwards.length) {
      const existing = bucketLines.get('Awards') || [];
      bucketLines.set('Awards', [...existing, ...toAwards]);
    }
  }

  // 2) Work Experience: extract embedded education section header and everything after it into Education.
  //    Resumes often have "Professional Education and Certification" appended at the end of the experience block.
  const expLines = bucketLines.get('Work Experience');
  if (expLines?.length) {
    const EDU_SECTION_HEADER = /^(professional\s+education(\s+and\s+certifications?)?|education\s+and\s+certifications?|academic\s+background|professional\s+education|qualifications?\s+&\s+certifications?)/i;
    // Also detect split headers: standalone "Education" line followed by "and Certification" or "Certification:" on next line
    const EDU_SECTION_HEADER_SPLIT = /^education$/i;
    const eduHeaderIdx = expLines.findIndex((l, idx) => {
      const t = l.trim();
      if (EDU_SECTION_HEADER.test(t)) return true;
      // Two-line split: "Education\nand Certification" or "Education\nCertification:"
      if (EDU_SECTION_HEADER_SPLIT.test(t)) {
        const next = (expLines[idx + 1] || '').trim().toLowerCase();
        if (next.startsWith('and cert') || next.startsWith('cert') || next === '') return true;
      }
      return false;
    });
    if (eduHeaderIdx !== -1) {
      // Everything BEFORE the header stays in Work Experience; the header + rest go to Education
      const keepExp = expLines.slice(0, eduHeaderIdx);
      const eduContent = expLines.slice(eduHeaderIdx + 1); // skip header line itself
      bucketLines.set('Work Experience', keepExp);
      if (eduContent.length) {
        const existing = bucketLines.get('Education') || [];
        bucketLines.set('Education', [...existing, ...eduContent]);
      }
    } else {
      // Fallback: move clearly-education and certification lines that crept into Work Experience
      const keepExp: string[] = [];
      const toEduFromExp: string[] = [];
      const toCertFromExp: string[] = [];
      const EDU_LINE = /\b(pursuing|completed|graduated|b\.e|b\.tech|mba|msc|bsc|m\.tech|phd|diploma|executive\s+mba|isb|iim|iit|c-dac|ambedkar|university|college|institute)\b/i;
      const CERT_LINE = /\b(certified|certification|nanodegree|nano\s*degree|scjp|credential|dmnd|program\s*\(|sun\s+certified)\b/i;
      for (const line of expLines) {
        const t = line.trim();
        // Skip orphan section header labels that leaked through
        if (/^(education|certification|certifications|professional\s+education)[\s:]*$/i.test(t)) continue;
        // Keep job-dated lines even if they incidentally mention "university"
        const looksLikeJobLine = /\b\d{4}\s*[-–]\s*(till\s*date|\d{4}|present)/i.test(line) ||
                                  /\bat\s+[A-Z]|\s+\|\s+[A-Z]/.test(line);
        if (!looksLikeJobLine && CERT_LINE.test(line) && !EDU_LINE.test(line)) {
          toCertFromExp.push(line);
        } else if (!looksLikeJobLine && EDU_LINE.test(line)) {
          toEduFromExp.push(line);
        } else {
          keepExp.push(line);
        }
      }
      bucketLines.set('Work Experience', keepExp);
      if (toEduFromExp.length) {
        const existing = bucketLines.get('Education') || [];
        bucketLines.set('Education', [...existing, ...toEduFromExp]);
      }
      if (toCertFromExp.length) {
        const existing = bucketLines.get('Certifications') || [];
        bucketLines.set('Certifications', [...existing, ...toCertFromExp]);
      }
    }
  }

  // 3) Certifications: move education lines to Education (ISB, MBA, degree, %, diploma)
  //    AND extract any "Language:" section that landed here.
  const certLines = bucketLines.get('Certifications');
  if (certLines?.length) {
    const keepCert: string[] = [];
    const toEdu: string[] = [];
    const toLangFromCert: string[] = [];
    const EDU_PATTERN = /\b(isb|iim|iit|mba|b\.e|b\.tech|msc|bsc|university|college|institute|degree|diploma|graduat|c-dac|ambedkar|executive\s+mba)/i;
    const LANG_HEADER = /^languages?\s*:?\s*$/i;
    const LANG_NAMES = /\b(english|hindi|portuguese|spanish|french|german|japanese|chinese|arabic|dutch|italian|russian|korean|norwegian)\b/i;
    let inLangSection = false;
    for (const line of certLines) {
      const t = line.trim();
      // Detect "Language:" standalone header → everything after is language lines
      if (LANG_HEADER.test(t)) { inLangSection = true; continue; }
      if (inLangSection) { toLangFromCert.push(line); continue; }
      // Detect comma-separated language list on a single line (e.g. "English , Hindi , Portuguese & Spanish ( Basic)")
      if (LANG_NAMES.test(t) && !EDU_PATTERN.test(t) && t.length < 120) {
        toLangFromCert.push(line);
        continue;
      }
      if (EDU_PATTERN.test(t) || (/^Sep\s*'?\d{2}$|^\d{4}$/.test(t) && certLines.some((l) => /isb|mba|product\s*management/i.test(l)))) {
        toEdu.push(line);
      } else {
        keepCert.push(line);
      }
    }
    bucketLines.set('Certifications', keepCert);
    if (toEdu.length) {
      const existing = bucketLines.get('Education') || [];
      bucketLines.set('Education', [...existing, ...toEdu]);
    }
    if (toLangFromCert.length) {
      const existing = bucketLines.get('Languages') || [];
      bucketLines.set('Languages', [...existing, ...toLangFromCert]);
    }
  }

  // 4) Skills: move spoken-language-only lines (single or comma-separated) to Languages bucket.
  //    Also filter out orphan section-header fragments ("Languages &", "AI/ML", "Professional", "Cloud")
  //    that leak into Skills when resume sub-headers like "Languages & Frameworks" get split during parsing.
  const skillsLines = bucketLines.get('Skills');
  if (skillsLines?.length) {
    const keepSkills: string[] = [];
    const toLang: string[] = [];
    const LANG_NAMES_RE = /\b(english|spanish|portuguese|french|german|hindi|chinese|japanese|arabic|dutch|italian|russian|korean|norwegian|swedish|danish|polish|turkish|malay|tagalog)\b/gi;
    // Allow optional spaces inside parens: ( Basic ), (Fluent), etc.
    const PROFICIENCY_RE = /\(\s*(basic|fluent|native|proficient|intermediate|advanced)\s*\)/gi;
    // Orphan fragment patterns: short lines that are residual section-header labels, not actual skill content
    const ORPHAN_FRAGMENT_RE = /^(languages\s*&?\s*$|ai\s*\/?\s*ml\s*$|cloud\s*$|professional\s*$|technical\s*$|frameworks?\s*$|technologies?\s*$|tools\s*$|skills\s*$|certifications?\s*$|education\s*$|experience\s*$|summary\s*$)/i;
    const isLangOnlyLine = (line: string): boolean => {
      const t = line.trim();
      if (!t) return false;
      // Strip language names and proficiency markers; what's left should be only punctuation/connectors
      const stripped = t
        .replace(LANG_NAMES_RE, '')
        .replace(PROFICIENCY_RE, '')
        .replace(/[\s,|&()\-–]/g, '')
        .trim();
      const matchCount = (t.match(LANG_NAMES_RE) || []).length;
      return matchCount >= 1 && stripped.length === 0 && t.length < 150;
    };
    const toExpFromSkills: string[] = [];
    for (const line of skillsLines) {
      const t = line.trim();
      // Drop orphan section-header fragments (parsing artifacts)
      if (ORPHAN_FRAGMENT_RE.test(t) && t.length <= 25) continue;
      if (isJobHistoryLine(t)) {
        toExpFromSkills.push(line);
      } else if (isLangOnlyLine(line)) {
        toLang.push(line);
      } else {
        keepSkills.push(line);
      }
    }
    bucketLines.set('Skills', keepSkills);
    if (toLang.length) {
      const existing = bucketLines.get('Languages') || [];
      bucketLines.set('Languages', [...existing, ...toLang]);
    }
    if (toExpFromSkills.length) {
      const existing = bucketLines.get('Work Experience') || [];
      bucketLines.set('Work Experience', [...existing, ...toExpFromSkills]);
    }
  }

  const out: ResumeSection[] = [];
  for (const bucket of CANONICAL_SECTION_BUCKETS) {
    const lines = bucketLines.get(bucket);
    if (!lines?.length) continue;
    const content = lines.join('\n').trim();
    if (!content) continue;
    out.push({
      title: bucket,
      content,
      preview: content.slice(0, 120).replace(/\n/g, ' ') + (content.length > 120 ? '…' : ''),
    });
  }
  return out.length ? out : sections;
}

/**
 * Move contact lines (email, phone, etc.) from Professional Summary into Personal Information.
 * Use after getting sections from LLM/review so contact does not appear under Professional Summary in the preview.
 */
export function moveContactFromSummaryToPersonalInfo(sections: ResumeSection[]): ResumeSection[] {
  if (!sections?.length) return sections;
  const summaryKey = 'Professional Summary';
  const personalKeys = ['Personal Information', 'Contact', 'Contact Information'];
  const summaryIdx = sections.findIndex((s) => s.title === summaryKey || s.title.toLowerCase() === 'professional summary');
  const personalIdx = sections.findIndex((s) => personalKeys.some((k) => k.toLowerCase() === s.title.toLowerCase()));
  if (summaryIdx === -1) return sections;
  const summary = sections[summaryIdx];
  const lines = summary.content.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const toContact: string[] = [];
  const restSummary: string[] = [];
  let nameMoved = false;
  for (const line of lines) {
    if (looksLikeName(line) && !nameMoved) {
      toContact.push(line);
      nameMoved = true;
      continue;
    }
    if (looksLikeName(line) && nameMoved) {
      restSummary.push(line);
      continue;
    }
    if (looksLikeContactLine(line)) {
      toContact.push(line);
      continue;
    }
    restSummary.push(line);
  }
  if (toContact.length === 0) return sections;
  const result = sections.map((s, i) => {
    if (i === summaryIdx) {
      return { ...s, content: restSummary.join('\n').trim(), preview: restSummary.join(' ').slice(0, 120) + (restSummary.join(' ').length > 120 ? '…' : '') };
    }
    if (personalIdx !== -1 && i === personalIdx) {
      const existing = sections[personalIdx].content.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      const seen = new Set<string>();
      const merged: string[] = [];
      for (const line of [...existing, ...toContact]) {
        const norm = line.toLowerCase().replace(/\s+/g, ' ').trim();
        if (norm && !seen.has(norm)) {
          seen.add(norm);
          merged.push(line);
        }
      }
      const content = merged.join('\n').trim();
      return { ...s, content, preview: content.slice(0, 120).replace(/\n/g, ' ') + (content.length > 120 ? '…' : '') };
    }
    return s;
  });
  if (personalIdx === -1) {
    const newPersonal: ResumeSection = {
      title: 'Personal Information',
      content: toContact.join('\n'),
      preview: toContact.join(' ').slice(0, 120) + (toContact.join(' ').length > 120 ? '…' : ''),
    };
    result.splice(0, 0, newPersonal);
  }
  return sortSectionsByCanonicalOrder(result);
}

/**
 * Classify a single line of resume content into the correct bucket (content-based, like rule-based NER).
 * Order of checks matters: most specific patterns first.
 */
function classifyLineToBucket(line: string): BucketName {
  const t = line.trim();
  const lower = t.toLowerCase();
  if (!t) return 'Additional Information';

  if (isJobHistoryLine(t)) return 'Work Experience';
  if (looksLikeToolListLine(t)) return 'Skills';

  // Personal Information: contact only
  if (/^[\w.-]+@[\w.-]+\.\w+/.test(t) || (/^(email|e-mail|phone|tel|mobile|linkedin|linked in|website|portfolio)\s*:?\s*/i.test(t) && t.length < 80)) return 'Personal Information';
  // Phone / URL detection – use explicit formats only (+ prefix, 10-digit blocks, social URLs) to avoid matching date ranges like "2011 - 12/2015"
  if (/(linkedin\.com|github\.com|http)/i.test(t) && t.length < 120) return 'Personal Information';
  if (/(\+\d[\d\s\-().]{8,}|\b\d{10}\b)/.test(t) && t.length < 120 && !/\b\d{4}\s*[-–]\s*(till\s*date|present|\d{2}\/\d{4}|\d{4})\b/i.test(t)) return 'Personal Information';
  if (/^[A-Z][a-z]+\s+[A-Z][a-z]+(\s+[A-Z][a-z]+)?$/.test(t) && t.length < 40 && !/@|http|\.com|java|spring|python/i.test(t)) return 'Personal Information';

  // Education: degrees, institutions, years
  if (/\b(mba|b\.e|b\.tech|msc|bsc|m\.tech|phd|diploma|graduat|university|college|institute|isb|iim|iit|c-dac|ambedkar)\b/i.test(lower)) return 'Education';
  if (/\b(pursuing|from)\s+.+\s+(in|from)\s+\d{4}/i.test(t) || /\d{4}\s*[-–]\s*\d{4}|'\d{2}\b/.test(t)) {
    if (lower.includes('university') || lower.includes('college') || lower.includes('mba') || lower.includes('degree') || lower.includes('diploma') || lower.includes('%')) return 'Education';
  }

  // Certifications
  if (/\b(certified|certification|nanodegree|nano\s*degree|scjp|credential|dmnd)\b/i.test(lower)) return 'Certifications';
  if (/^certification\s*:/i.test(t)) return 'Certifications';

  // Awards: company awards, won, stellar (keep before Experience so award lines don't match job verbs)
  if (/\b(won\s+multiple|stellar\s+success|award\s+for|contribution\s+in)\b/i.test(lower)) return 'Awards';
  if (/\b(netcracker|tech mahindra).*(award|stellar|won|contribution)\b/i.test(lower)) return 'Awards';
  if (/\b(british telecom|at&t).*(delivery|contribution)\b/i.test(lower) && t.length < 100) return 'Awards';

  // Work Experience: job headers (title at company (dates)), action bullets, project/role descriptions
  // Job header: "Senior PM at Company (2020 – 2024)" or "Engineer | Company" or "Company – Role"
  if (/\s+at\s+[A-Z]/.test(t) && (/\d{4}\s*[-–]\s*\d{4}|\(\d{4}\)|'\d{2}\b/.test(t) || t.length < 90)) return 'Work Experience';
  if (/\s+\|\s+[A-Z]|\s+[-–]\s+[A-Z]/.test(t) && t.length < 100) return 'Work Experience';
  if (/^[A-Z][a-z].*(\(?\d{4}\s*[-–]\s*\d{4}\)?|\(\d{4}\s*[-–]\s*present\))/i.test(t) && !lower.includes('university') && !lower.includes('mba') && !lower.includes('degree')) return 'Work Experience';

  // Bullet lines (• or -) that are substantive → almost always experience/projects
  if (/^[•*\-]\s*.{15,}/.test(t)) return 'Work Experience';

  // Action verbs and outcome phrases (n number of experiences – catch all common patterns)
  const experienceVerbs = /\b(developed|created|built|delivered|provided|led|managed|designed|implemented|enabling|ensuring|integrat|drove|achieved|reduced|improved|launched|established|coordinated|supported|handled|owned|defined|spearheaded|championed|headed|responsible for|accountable for|key (achievement|deliverable)|shipped|released|deployed|migrated|automated|optimized|scaled|grew|increased|decreased|transformed|pioneered|introduced|facilitated|orchestrat|collaborated|aligned|executed|oversaw|mentored|analysed|analyzed|resolved|architected|worked|conducted|performed|maintained|produced|solved|negotiated|presented|trained|advised|influenced)\b/i;
  if (experienceVerbs.test(lower) && t.length > 20) return 'Work Experience';
  if (/\b(socket-based|binary comparator|notification server|web scraping|patch (tuesday|information)|microservices|roadmap|stakeholder|sprint|backlog|agile)\b/i.test(lower) && t.length > 25) return 'Work Experience';
  // Long sentence that sounds like role description (has "to " or "for " or "by " with verb)
  if (t.length > 50 && /\b(to|for|by|with)\s+(enable|ensure|support|deliver|improve|reduce|increase|achieve|drive)\b/i.test(lower)) return 'Work Experience';

  // Human languages (natural languages) with proficiency / global context
  if (/\b(japanese|chinese|german|french|norwegian|spanish|english|hindi|arabic|portuguese|dutch|italian|russian|korean)\b/i.test(lower) && (/\b(fluent|basic|proficient|native|intermediate|advanced|enhancing|global)\b/i.test(lower) || t.length < 80)) return 'Languages';

  // Skills: tech stack, tools, short lists (including when under wrong heading)
  const techSkillPattern = /\b(java|spring|html5|css3|yaml|json|xml|rest|soap|wsdl|argo|langchain|llm|ai\b|ml\b|rag|safe|figma|miro|productboard|jira|asana|balsamiq|hibernate|bash|go\b|transformers|sentiment\s+analysis|perceptual\s+maps|unsupervised)\b/i;
  if (techSkillPattern.test(lower)) return 'Skills';
  if (/\b(telecom\s+bss|oss\b|core\s+platform|poc\b|opf\s*&\s*orchestration)\b/i.test(lower)) return 'Skills';
  if (/\b(functional\s+and\s+domain|product\s+centric|grip\s+on\s+building|engagement\s+with\s+customers)\b/i.test(lower)) return 'Skills';
  if (t.length <= 60 && /^[A-Z][a-zA-Z0-9\s,&\-/]+$/.test(t) && !/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s/i.test(t)) return 'Skills'; // short title-case list line

  // Single tech token (JIRA, Java, etc.) or very short tech line
  if (t.length <= 25 && /^[A-Za-z0-9\s,&\-]+$/.test(t)) return 'Skills';

  // Professional Summary: first-person or "experienced" / "years of" without tech keywords
  if (/\b(i\s+am|i\s+have|years?\s+of\s+experience|experienced\s+(in|with)|seeking\s+to|passionate\s+about)\b/i.test(lower) && t.length > 40 && !/\b(java|spring|python|html|css|api|yaml|json|xml|rest|jira)\b/i.test(lower)) return 'Professional Summary';

  // Long prose (sentence-like, not a list) → prefer Work Experience over Additional
  if (t.length >= 35 && /[a-z]/.test(t) && (t.split(',').length <= 2 || t.length > 80)) return 'Work Experience';
  // Past tense / -ed word often indicates experience bullet
  if (t.length >= 25 && /\b\w+ed\b/.test(lower) && !lower.includes('university') && !lower.includes('certified')) return 'Work Experience';

  // Default: only truly short/generic lines go to Additional
  return 'Additional Information';
}

/** Split a long or blended line into smaller fragments so each can be classified correctly. */
function splitContentIntoLines(content: string): string[] {
  const lines: string[] = [];
  const byNewline = content.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  for (const line of byNewline) {
    if (line.length <= 200) {
      lines.push(line);
      continue;
    }
    const parts = line.split(/\s+[•*\-]\s+|\s+\.\s+(?=[A-Z])/).map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) lines.push(...parts);
    else lines.push(line);
  }
  return lines;
}

/**
 * Return the correct bucket only when we're HIGH-CONFIDENT (clear pattern match).
 * Used for "cleanup only": move a line only if it's clearly in the wrong section.
 * Returns null when we should trust the current section (don't move).
 */
function getHighConfidenceBucket(line: string): BucketName | null {
  const t = line.trim();
  const lower = t.toLowerCase();
  if (!t) return null;
  // Exclude lines that look like work-experience job headers (company + location + date) to avoid false phone-number hits
  const looksLikeJobHeader = /\b\d{4}\s*[-–]\s*(till\s*date|present|\d{2}\/\d{4}|\d{4})\b/i.test(t) ||
    /\b\d{2}\/\d{4}\b/.test(t) ||
    (/\b(at|bangalore|pune|mumbai|india|usa|uk|london|new\s+york)\b/i.test(lower) && /\b\d{4}\b/.test(t));
  if (!looksLikeJobHeader) {
    if (/^[\w.-]+@[\w.-]+\.\w+/.test(t) || (/^(email|phone|tel|mobile|linkedin)\s*:?\s*/i.test(t) && t.length < 80)) return 'Personal Information';
    // Phone / URL detection – require explicit phone-number format (+XX, 10+ digit block) or social links; exclude date ranges
    if (/(linkedin\.com|github\.com)/i.test(t) && t.length < 120) return 'Personal Information';
    if (/(\+\d[\d\s\-().]{8,}|\b\d{10}\b)/.test(t) && t.length < 120) return 'Personal Information';
  }
  if (/\b(mba|b\.e|b\.tech|university|college|isb|iim|degree|diploma|graduat)\b/i.test(lower)) return 'Education';
  if (/\b(certified|certification|nanodegree|scjp|credential)\b/i.test(lower)) return 'Certifications';
  if (/\b(won|stellar\s+success|award\s+for)\b/i.test(lower)) return 'Awards';
  if (isJobHistoryLine(t)) return 'Work Experience';
  if (looksLikeToolListLine(t) || DESIGN_OR_TOOL_RE.test(t)) return 'Skills';
  if (/\b(japanese|chinese|french|german|spanish|english|portuguese|hindi|arabic|dutch|italian|russian|korean)\b/i.test(lower) && (/\b(fluent|basic|proficient|native|intermediate|advanced)\b/i.test(lower) || t.length < 60)) return 'Languages';
  if (t.length <= 50 && /^[A-Za-z0-9\s,&\-]+$/.test(t) && /\b(java|spring|python|html|css|jira|api)\b/i.test(lower)) return 'Skills';
  return null;
}

/**
 * Header-first segmentation (research-backed): trust document structure first, then only move clearly misplaced lines.
 * - Step 1: Assign each section's content to the bucket indicated by its TITLE (preserves layout/structure).
 * - Step 2: Within each bucket, only REASSIGN lines that are high-confidence belonging elsewhere (e.g. cert in Languages → Certifications).
 * This avoids scattering: we don't reclassify every line from scratch; we trust the header unless content is clearly wrong.
 */
export function headerAwareSegregate(sections: ResumeSection[]): ResumeSection[] {
  if (!sections?.length) return sections;

  const bucketLines = new Map<BucketName, string[]>();

  for (const section of sections) {
    const bucket = resolveBucket(section.title) || 'Additional Information';
    const lines = splitContentIntoLines(section.content).filter((l) => l.trim());
    for (const line of lines) {
      const list = bucketLines.get(bucket) || [];
      list.push(line);
      bucketLines.set(bucket, list);
    }
  }

  // Only move lines out of "unsure" buckets; never scatter content from Work Experience, Education, Skills, Professional Summary
  const TRUST_BUCKETS: BucketName[] = ['Work Experience', 'Education', 'Skills', 'Professional Summary', 'Personal Information', 'Certifications', 'Awards', 'Projects', 'References', 'Languages', 'Languages & Frameworks'];
  const resultBuckets = new Map<BucketName, string[]>();
  for (const [bucket, lines] of bucketLines) {
    for (const line of lines) {
      let dest = bucket;
      const target = getHighConfidenceBucket(line);
      const trustSource = TRUST_BUCKETS.includes(bucket);
      if (target !== null && target !== bucket) {
        if (!trustSource) dest = target;
        else if (target === 'Personal Information') dest = target;
        else if (bucket === 'Professional Summary' && target === 'Work Experience') dest = target;
        else if (bucket === 'Skills' && target === 'Work Experience') dest = target;
        else if (bucket === 'Personal Information' && (target === 'Skills' || target === 'Work Experience' || target === 'Professional Summary')) dest = target;
      } else if (bucket === 'Languages & Frameworks') {
        const kind = classifyContentLine(line);
        if (kind === 'certification') dest = 'Certifications';
        else if (kind === 'education') dest = 'Education';
        else if (kind === 'work_award') dest = 'Awards';
        else if (kind === 'language') dest = 'Languages';
      }
      if (dest !== bucket) console.log(`    [reassign] "${line.slice(0, 50)}${line.length > 50 ? '…' : ''}" ${bucket} → ${dest}`);
      const list = resultBuckets.get(dest) || [];
      list.push(line);
      resultBuckets.set(dest, list);
    }
  }

  const result: ResumeSection[] = [];
  for (const bucket of CANONICAL_SECTION_BUCKETS) {
    const lines = resultBuckets.get(bucket);
    if (!lines?.length) continue;
    const content = lines.join('\n').trim();
    if (!content) continue;
    result.push({
      title: bucket,
      content,
      preview: content.slice(0, 120).replace(/\n/g, ' ') + (content.length > 120 ? '…' : ''),
    });
  }
  return result;
}

/**
 * Content-based segregation: classify every line by content and put it in the correct bucket.
 * Use when header structure is missing or unreliable (e.g. one giant section).
 */
export function segregateResumeContent(sections: ResumeSection[]): ResumeSection[] {
  if (!sections?.length) return sections;

  const bucketLines = new Map<BucketName, string[]>();

  for (const section of sections) {
    const lines = splitContentIntoLines(section.content);
    for (const line of lines) {
      if (!line.trim()) continue;
      const bucket = classifyLineToBucket(line);
      const list = bucketLines.get(bucket) || [];
      list.push(line);
      bucketLines.set(bucket, list);
    }
  }

  const result: ResumeSection[] = [];
  for (const bucket of CANONICAL_SECTION_BUCKETS) {
    const lines = bucketLines.get(bucket);
    if (!lines?.length) continue;
    const content = lines.join('\n').trim();
    if (!content) continue;
    result.push({
      title: bucket,
      content,
      preview: content.slice(0, 120).replace(/\n/g, ' ') + (content.length > 120 ? '…' : ''),
    });
  }
  return result;
}

/**
 * Hybrid pipeline (research-backed): header-first, then content-based cleanup.
 * Use header-aware when we have multiple sections with distinct titles; fall back to full content segregation when structure is missing (e.g. one section has most content).
 */
export function arrangeResumeSections(sections: ResumeSection[]): ResumeSection[] {
  if (!sections?.length) return sections;

  const normalized = normalizeResumeSections(sections);
  const totalLines = normalized.reduce((sum, s) => sum + s.content.split(/\r?\n/).filter((l) => l.trim()).length, 0);
  const maxSectionLines = Math.max(...normalized.map((s) => s.content.split(/\r?\n/).filter((l) => l.trim()).length), 0);

  const hasStructure = normalized.length >= 2 && maxSectionLines <= Math.max(totalLines * 0.7, 1);
  console.log('[arrangeResumeSections]', hasStructure ? 'Header-aware (trust section titles)' : 'Content-based (classify every line)', '| sections:', normalized.length, 'totalLines:', totalLines);
  if (hasStructure) {
    return headerAwareSegregate(normalized);
  }
  return segregateResumeContent(normalized);
}
